import React from 'react';
import { ArrowUpRight, Compass, Network, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import useYamlData from '../hooks/useYamlData';
import SystemMark from '../components/SystemMark';
import '../styles/Home.css';

/*
 * The hero system: one core, with the nine hats orbiting it across three ring
 * planes. Decorative only -- nothing here is selectable; the nine are
 * explorable on the In Practice page.
 */
const HERO_RINGS = [
  { tilt: -18, rx: 84, ry: 29, period: 26 },
  { tilt: 28, rx: 70, ry: 24, period: 34 },
  { tilt: -62, rx: 78, ry: 20, period: 44 },
];

const heroRings = (hats) => {
  const max = Math.max(...hats.map((hat) => hat.count), 1);
  return HERO_RINGS.map((ring, ringIndex) => ({
    ...ring,
    color: 'var(--tmc-signal-systems)',
    satellites: hats
      .filter((_, index) => index % HERO_RINGS.length === ringIndex)
      .map((hat, index, all) => ({
        angle: (360 / all.length) * index + ringIndex * 37,
        color: `var(--tmc-hat-${hat.id})`,
        r: 3.5 + Math.sqrt(hat.count / max) * 3.5,
      })),
  }));
};

// Disciplines are ordered in portfolio.yaml; icons track that order.
const DISCIPLINE_ICONS = [Network, Compass, ShieldCheck, ArrowUpRight];

const PATHS = [
  { to: '/in-practice', label: 'One year, nine hats' },
  { to: '/career', label: 'Career atlas' },
  { to: '/resume', label: 'Full resume' },
  { to: '/homelab', label: 'Homelab field guide' },
];

const Home = () => {
  const { data, loading, error } = useYamlData('/data/portfolio.yaml');
  // The nine worlds are the In Practice system; the hero borrows it so the landing
  // page and that story are visibly the same thing.
  const { data: inPractice } = useYamlData('/data/in-practice.yaml');

  if (loading) return <p className="tmc-state">Loading portfolio...</p>;
  if (error) return <p className="tmc-state tmc-state--error">{error}</p>;

  return (
    <div className="home-page">
      <section className="home-hero page-shell">
        <div className="hero-copy">
          <p className="eyebrow">TMCWeb / Portfolio</p>
          <h1>{data.identity.name}</h1>
          <p className="hero-role">{data.identity.role}</p>
          <p className="hero-statement">{data.identity.statement}</p>
          <div className="hero-actions">
            <Link to="/career" className="action-link">
              Explore career <ArrowUpRight size={18} aria-hidden="true" />
            </Link>
            <a href={`mailto:${data.contact.email}`} className="quiet-link">
              Start a conversation
            </a>
          </div>
        </div>
        <div className="hero-object">
          <SystemMark
            title={
              inPractice?.hats
                ? `${inPractice.hats.length} hats in orbit`
                : undefined
            }
            coreRadius={54}
            surface="bands"
            color="var(--tmc-brand-core)"
            tilt={-14}
            rings={inPractice?.hats ? heroRings(inPractice.hats) : []}
          />
        </div>
      </section>

      <section className="signal-band page-shell" aria-label="Career signals">
        {data.signals.map((signal) => (
          <div className="signal" key={signal.label}>
            <strong>{signal.value}</strong>
            <span>{signal.label}</span>
          </div>
        ))}
      </section>

      <section className="discipline-section page-shell">
        <div className="section-intro">
          <p className="eyebrow">Practice</p>
          <h2>Leading through systems, not slogans.</h2>
        </div>
        <div className="discipline-grid">
          {data.disciplines.map((discipline, index) => {
            const Icon = DISCIPLINE_ICONS[index] ?? Network;
            return (
              <article
                className={`tmc-card discipline discipline--${discipline.tone}`}
                key={discipline.name}
              >
                <Icon size={22} aria-hidden="true" />
                {/* One word per line: every discipline is two words, so an
                    explicit break keeps all four titles the same shape rather
                    than leaving it to where the text happens to wrap. */}
                <h3>
                  {discipline.name.split(' ').map((word) => (
                    // Trailing space keeps the accessible name intact ("Delivery
                    // systems", not "Deliverysystems"); it collapses visually
                    // because the spans are block level.
                    <span key={word}>{word} </span>
                  ))}
                </h3>
                <p>{discipline.summary}</p>
              </article>
            );
          })}
        </div>
      </section>

      <nav className="home-paths page-shell" aria-label="Site sections">
        {PATHS.map((path, index) => (
          <Link to={path.to} key={path.to}>
            <span>{String(index + 1).padStart(2, '0')}</span>
            <strong>{path.label}</strong>
            <ArrowUpRight aria-hidden="true" />
          </Link>
        ))}
      </nav>
    </div>
  );
};

export default Home;
